// src/services/decisionsService.ts
// Service layer for unified decisions (Threads + Calls + Projects)

import { supabase, isDemoMode } from '../lib/supabase';
import type { 
  Decision, 
  DecisionWithUsers,
  DecisionIntegritySummary,
  DecisionIntegrityVerification,
  ProposeDecisionPayload,
  SupersedeDecisionPayload
} from '../types/decisions';
import { createDemoParticipantNotification } from '../demo/demoProjectParticipantStore';

// ─────────────────────────────────────────────────────────────────────────────
// Demo Mode In-Memory Store
// ─────────────────────────────────────────────────────────────────────────────

// Demo decisions stored in memory (with user names for display)
const demoDecisions: Map<string, DecisionWithUsers> = new Map();

// Initialize with demo data for projects
// Uses project IDs matching the demo projects in useProjects.ts ('1', '2', '3', etc.)
const DEMO_PROJECT_DECISIONS: DecisionWithUsers[] = [
  {
    id: 'demo-decision-1',
    body: 'Payment gateway integration will use Stripe as the primary processor with PayPal as a fallback option. @Sarah Johnson to finalize the API contracts.',
    title: null,
    project_id: '1',
    workspace_id: null,
    source_thread_id: 'thread-101',
    source_message_id: null,
    source_call_session_id: null,
    status: 'decided',
    proposed_by: 'user-1',
    proposed_by_name: 'Alex Rivera',
    proposed_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-2',
    decision_owner_name: 'Sarah Johnson',
    decided_by: 'user-2',
    decided_by_name: 'Sarah Johnson',
    decided_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    locked_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'demo-decision-2',
    body: 'Launch date moved to Q2 2026 to accommodate additional security testing requirements.',
    title: null,
    project_id: '1',
    workspace_id: null,
    source_thread_id: null,
    source_message_id: null,
    source_call_session_id: 'call-201',
    status: 'decided',
    proposed_by: 'user-3',
    proposed_by_name: 'Emily Vendor Lead',
    proposed_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-1',
    decision_owner_name: 'Alex Rivera',
    decided_by: 'user-1',
    decided_by_name: 'Alex Rivera',
    decided_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    locked_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'demo-decision-3',
    body: 'User authentication will implement OAuth 2.0 with support for Google, Microsoft, and Apple sign-in. @Emily Vendor Lead to review security implications.',
    title: null,
    metadata: { dependsOnDecisionIds: ['demo-decision-1'] },
    project_id: '1',
    workspace_id: null,
    source_thread_id: 'thread-102',
    source_message_id: null,
    source_call_session_id: null,
    status: 'proposed',
    proposed_by: 'user-2',
    proposed_by_name: 'Sarah Johnson',
    proposed_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-4',
    decision_owner_name: 'Emily Watson',
    decided_by: null,
    decided_at: null,
    locked_at: null,
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'demo-decision-4',
    body: 'Database architecture will use PostgreSQL with read replicas for scalability.',
    title: null,
    project_id: '1',
    workspace_id: null,
    source_thread_id: 'thread-103',
    source_message_id: null,
    source_call_session_id: null,
    status: 'proposed',
    proposed_by: 'user-4',
    proposed_by_name: 'Emily Watson',
    proposed_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-1',
    decision_owner_name: 'Alex Rivera',
    decided_by: null,
    decided_at: null,
    locked_at: null,
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'demo-decision-5',
    body: 'Initial budget allocation of $150,000 for Phase 1 development approved.',
    title: null,
    project_id: '1',
    workspace_id: null,
    source_thread_id: null,
    source_message_id: null,
    source_call_session_id: 'call-202',
    status: 'superseded',
    proposed_by: 'user-1',
    proposed_by_name: 'Alex Rivera',
    proposed_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-1',
    decision_owner_name: 'Alex Rivera',
    decided_by: 'user-1',
    decided_by_name: 'Alex Rivera',
    decided_at: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString(),
    locked_at: null,
    superseded_by_decision_id: 'demo-decision-6',
    supersede_reason: 'Budget increased due to expanded scope',
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'demo-decision-6',
    body: 'Revised budget allocation of $200,000 for Phase 1 development to include mobile app.',
    title: null,
    metadata: { dependsOnDecisionIds: ['demo-decision-2'] },
    project_id: '1',
    workspace_id: null,
    source_thread_id: 'thread-104',
    source_message_id: null,
    source_call_session_id: null,
    status: 'decided',
    proposed_by: 'user-1',
    proposed_by_name: 'Alex Rivera',
    proposed_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-1',
    decision_owner_name: 'Alex Rivera',
    decided_by: 'user-1',
    decided_by_name: 'Alex Rivera',
    decided_at: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    locked_at: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // Decisions for project 2
  {
    id: 'demo-decision-7',
    body: 'Pricing model will use tiered subscription with volume discounts for enterprise clients.',
    title: null,
    project_id: '2',
    workspace_id: null,
    source_thread_id: 'thread-201',
    source_message_id: null,
    source_call_session_id: null,
    status: 'decided',
    proposed_by: 'user-2',
    proposed_by_name: 'Sarah Johnson',
    proposed_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-1',
    decision_owner_name: 'Alex Rivera',
    decided_by: 'user-1',
    decided_by_name: 'Alex Rivera',
    decided_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    locked_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'demo-decision-8',
    body: 'Partner integration requirements to include REST API documentation and sandbox environment.',
    title: null,
    project_id: '2',
    workspace_id: null,
    source_thread_id: null,
    source_message_id: null,
    source_call_session_id: 'call-301',
    status: 'proposed',
    proposed_by: 'user-3',
    proposed_by_name: 'Emily Vendor Lead',
    proposed_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-2',
    decision_owner_name: 'Sarah Johnson',
    decided_by: null,
    decided_at: null,
    locked_at: null,
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // Add an invalidated decision for history section
  {
    id: 'demo-decision-9',
    body: 'Initial plan to use MongoDB for all data storage.',
    title: null,
    project_id: '1',
    workspace_id: null,
    source_thread_id: 'thread-105',
    source_message_id: null,
    source_call_session_id: null,
    status: 'invalidated',
    proposed_by: 'user-3',
    proposed_by_name: 'Emily Vendor Lead',
    proposed_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    decision_owner_id: 'user-1',
    decision_owner_name: 'Alex Rivera',
    decided_by: 'user-1',
    decided_by_name: 'Alex Rivera',
    decided_at: new Date(Date.now() - 44 * 24 * 60 * 60 * 1000).toISOString(),
    locked_at: null,
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: 'user-1',
    invalidated_by_name: 'Alex Rivera',
    invalidated_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    invalidation_reason: 'Decision reversed after performance testing showed PostgreSQL better suited for our query patterns',
    created_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'showcase-decision-gate-1',
    title: 'Foundation works Gate review',
    body: 'Review the versioned Gate policy, independent engineer evidence, and KPI outcome before the Foundation works Capital Gate advances. This assignment is review-only and does not authorize a capital release or payment.',
    project_id: '1',
    workspace_id: 'ax1-showcase-workspace',
    source_thread_id: null,
    source_message_id: null,
    source_call_session_id: null,
    status: 'proposed',
    proposed_by: '1',
    proposed_by_name: 'Sarah Johnson',
    proposed_at: '2026-07-20T09:00:00.000Z',
    decision_owner_id: '17',
    decision_owner_name: 'Rachel Nguyen',
    decided_by: null,
    decided_at: null,
    locked_at: null,
    superseded_by_decision_id: null,
    supersede_reason: null,
    invalidated_by: null,
    invalidated_at: null,
    invalidation_reason: null,
    created_at: '2026-07-20T09:00:00.000Z',
    updated_at: '2026-07-21T14:30:00.000Z',
  },
];

// Initialize demo decisions on module load
DEMO_PROJECT_DECISIONS.forEach(d => demoDecisions.set(d.id, d));
const DEMO_DECISIONS_STORAGE = 'ax1:demo:decisions:v1';
if (isDemoMode && typeof localStorage !== 'undefined') {
  try {
    const stored = JSON.parse(localStorage.getItem(DEMO_DECISIONS_STORAGE) || '[]');
    if (Array.isArray(stored)) for (const decision of stored) {
      if (typeof decision.id === 'string' && typeof decision.body === 'string' && typeof decision.project_id === 'string'
        && ['proposed', 'decided', 'superseded', 'invalidated'].includes(decision.status)) demoDecisions.set(decision.id, decision);
    }
  } catch { /* Malformed demo data must not prevent the application from loading. */ }
}
function saveDemoDecision(id: string, decision: DecisionWithUsers): void {
  demoDecisions.set(id, decision);
  try { localStorage.setItem(DEMO_DECISIONS_STORAGE, JSON.stringify(Array.from(demoDecisions.values()))); }
  catch { /* The in-memory Demo remains usable when browser storage is unavailable. */ }
}

function generateId(): string {
  return crypto.randomUUID();
}

const textEncoder = new TextEncoder();
const SHA256_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stableSerialize(entry)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

function rotateRight(value: number, amount: number): number {
  return (value >>> amount) | (value << (32 - amount));
}

function sha256Fallback(bytes: Uint8Array): string {
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  const bitLength = bytes.length * 8;
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);

  const hash = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const words = new Uint32Array(64);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) words[index] = view.getUint32(offset + index * 4, false);
    for (let index = 16; index < 64; index += 1) {
      const s0 = rotateRight(words[index - 15], 7) ^ rotateRight(words[index - 15], 18) ^ (words[index - 15] >>> 3);
      const s1 = rotateRight(words[index - 2], 17) ^ rotateRight(words[index - 2], 19) ^ (words[index - 2] >>> 10);
      words[index] = (words[index - 16] + s0 + words[index - 7] + s1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = hash;
    for (let index = 0; index < 64; index += 1) {
      const sigma1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choose = (e & f) ^ (~e & g);
      const temp1 = (h + sigma1 + choose + SHA256_CONSTANTS[index] + words[index]) >>> 0;
      const sigma0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sigma0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    hash[0] = (hash[0] + a) >>> 0;
    hash[1] = (hash[1] + b) >>> 0;
    hash[2] = (hash[2] + c) >>> 0;
    hash[3] = (hash[3] + d) >>> 0;
    hash[4] = (hash[4] + e) >>> 0;
    hash[5] = (hash[5] + f) >>> 0;
    hash[6] = (hash[6] + g) >>> 0;
    hash[7] = (hash[7] + h) >>> 0;
  }

  return Array.from(hash, word => word.toString(16).padStart(8, '0')).join('');
}

async function sha256(value: unknown): Promise<string> {
  const bytes = textEncoder.encode(stableSerialize(value));
  if (crypto.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  }
  return sha256Fallback(bytes);
}

function demoSnapshot(decision: Decision, status = decision.status): Record<string, unknown> {
  return {
    id: decision.id,
    projectId: decision.project_id ?? null,
    title: decision.title ?? null,
    description: decision.body,
    status,
    proposedBy: decision.proposed_by,
    proposedAt: decision.proposed_at,
    decisionOwnerId: decision.decision_owner_id ?? null,
    decidedBy: status === 'proposed' ? null : decision.decided_by ?? null,
    decidedAt: status === 'proposed' ? null : decision.decided_at ?? null,
    metadata: decision.metadata ?? {},
    createdAt: decision.created_at,
    updatedAt: decision.updated_at,
  };
}

async function buildDemoVerification(decision: Decision): Promise<DecisionIntegrityVerification> {
  const states = decision.status === 'proposed'
    ? [{ eventType: 'created' as const, snapshot: demoSnapshot(decision, 'proposed'), recordedAt: decision.created_at }]
    : [
        { eventType: 'created' as const, snapshot: demoSnapshot(decision, 'proposed'), recordedAt: decision.created_at },
        { eventType: decision.status as 'decided' | 'superseded' | 'invalidated', snapshot: demoSnapshot(decision), recordedAt: decision.updated_at },
      ];
  let previousHash: string | null = null;
  const records = [];
  for (const [index, state] of states.entries()) {
    const version = index + 1;
    const recordHash = await sha256({ decisionId: decision.id, version, previousHash, snapshot: state.snapshot });
    records.push({
      id: `${decision.id}-integrity-${version}`,
      version,
      eventType: state.eventType,
      actorId: version === 1 ? decision.proposed_by : decision.decided_by ?? decision.invalidated_by ?? decision.proposed_by,
      snapshot: state.snapshot,
      previousHash,
      recordHash,
      recordedAt: state.recordedAt,
      valid: true,
    });
    previousHash = recordHash;
  }
  return {
    decisionId: decision.id,
    valid: true,
    versionCount: records.length,
    latestHash: previousHash,
    verifiedAt: new Date().toISOString(),
    records: records.reverse(),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Service Result Type
// ─────────────────────────────────────────────────────────────────────────────

interface ServiceResult<T> {
  data: T | null;
  error: string | null;
}

/** Maps the canonical database contract to the UI decision contract. */
function mapCanonicalDecision(row: Record<string, unknown>): Decision {
  return {
    ...row,
    body: String(row.body ?? row.description ?? ''),
    proposed_at: String(row.proposed_at ?? row.created_at ?? new Date(0).toISOString()),
    decision_owner_id: (row.decision_owner_id as string | null | undefined) ?? null,
    superseded_by_decision_id: (row.superseded_by_decision_id as string | null | undefined) ?? null,
    supersede_reason: (row.supersede_reason as string | null | undefined) ?? null,
    invalidated_by: (row.invalidated_by as string | null | undefined) ?? null,
    invalidated_at: (row.invalidated_at as string | null | undefined) ?? null,
    invalidation_reason: (row.invalidation_reason as string | null | undefined) ?? null,
  } as Decision;
}

// ─────────────────────────────────────────────────────────────────────────────
// Decision Requested Notification Helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a decision_requested notification for the assigned decision owner.
 * This is separate from @mention notifications - it's a premium "action required" notification.
 */
async function createDecisionRequestedNotification(
  ownerId: string,
  actorId: string,
  decision: Decision,
): Promise<void> {
  // Skip if owner is same as actor
  if (ownerId === actorId) return;
  
  // Create preview text (truncate to 100 chars)
  const preview = decision.body.length > 100
    ? decision.body.slice(0, 97) + '...'
    : decision.body;
  const metadata = {
    decision_id: decision.id,
    workspace_id: decision.workspace_id,
    project_id: decision.project_id,
    thread_parent_id: decision.source_thread_id,
    message_id: decision.source_message_id,
    call_session_id: decision.source_call_session_id,
    preview,
    severity: 'warning',
  };
  
  if (isDemoMode) {
    createDemoParticipantNotification({
      userId: ownerId,
      actorId,
      type: 'decision_requested',
      title: 'Decision review required',
      body: preview,
      projectId: decision.project_id ?? 'unscoped',
      metadata,
    });
    return;
  }
  
  // Supabase mode: governed create via delivery RPC (canonical notifications schema)
  try {
    const { error } = await supabase.rpc('ax1_create_user_notification', {
      p_user_id: ownerId,
      p_type: 'decision_requested',
      p_title: 'Decision review required',
      p_body: preview,
      p_actor_id: actorId,
      p_entity_type: 'decision',
      p_entity_id: decision.id,
      p_metadata: metadata,
    });
    
    if (error) {
      console.error('[decisionsService] Failed to create decision_requested notification:', error);
    }
  } catch (err) {
    console.error('[decisionsService] Error creating decision_requested notification:', err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Propose Decision
// ─────────────────────────────────────────────────────────────────────────────

export async function proposeDecision(
  payload: ProposeDecisionPayload,
  currentUserId: string,
  _currentUserName?: string
): Promise<ServiceResult<Decision>> {
  // Demo mode: in-memory
  if (isDemoMode) {
    // Check for duplicate
    if (payload.source_message_id) {
      for (const d of demoDecisions.values()) {
        if (d.source_message_id === payload.source_message_id) {
          return { data: d, error: null };
        }
      }
    }
    
    const now = new Date().toISOString();
    const decision: Decision = {
      id: generateId(),
      body: payload.body,
      title: payload.title || null,
      project_id: payload.project_id || null,
      workspace_id: null,
      source_thread_id: payload.source_thread_id || null,
      source_message_id: payload.source_message_id || null,
      source_call_session_id: payload.source_call_session_id || null,
      status: 'proposed',
      proposed_by: currentUserId,
      proposed_at: now,
      decision_owner_id: payload.decision_owner_id || currentUserId,
      decided_by: null,
      decided_at: null,
      locked_at: null,
      superseded_by_decision_id: null,
      supersede_reason: null,
      invalidated_by: null,
      invalidated_at: null,
      invalidation_reason: null,
      created_at: now,
      updated_at: now,
    };
    
    saveDemoDecision(decision.id, decision);
    
    // Create decision_requested notification for owner (if different from proposer)
    if (payload.decision_owner_id && payload.decision_owner_id !== currentUserId) {
      await createDecisionRequestedNotification(
        payload.decision_owner_id,
        currentUserId,
        decision,
      );
    }
    
    return { data: decision, error: null };
  }

  // Supabase mode: call RPC
  try {
    const { data, error } = await supabase.rpc('propose_decision', {
      p_body: payload.body,
      p_title: payload.title || null,
      p_project_id: payload.project_id || null,
      p_source_thread_id: payload.source_thread_id || null,
      p_source_message_id: payload.source_message_id || null,
      p_source_call_session_id: payload.source_call_session_id || null,
      p_decision_owner_id: payload.decision_owner_id || null,
    });

    if (error) {
      return { data: null, error: error.message };
    }

    // Fetch the created/existing decision
    const decisionId = data as string;
    const result = await getDecisionById(decisionId);
    
    // Create decision_requested notification for owner (if different from proposer)
    if (result.data && payload.decision_owner_id && payload.decision_owner_id !== currentUserId) {
      await createDecisionRequestedNotification(
        payload.decision_owner_id,
        currentUserId,
        result.data,
      );
    }
    
    return result;
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Decide (Finalize) Decision
// ─────────────────────────────────────────────────────────────────────────────

export async function decideDecision(
  decisionId: string,
  currentUserId: string
): Promise<ServiceResult<Decision>> {
  // Demo mode
  if (isDemoMode) {
    const decision = demoDecisions.get(decisionId);
    if (!decision) {
      return { data: null, error: 'Decision not found' };
    }
    if (decision.status !== 'proposed') {
      return { data: null, error: 'Only proposed decisions can be decided' };
    }
    
    const now = new Date().toISOString();
    const updated: Decision = {
      ...decision,
      status: 'decided',
      decided_by: currentUserId,
      decided_at: now,
      locked_at: now,
      updated_at: now,
    };
    
    saveDemoDecision(decisionId, updated);
    if (decision.proposed_by !== currentUserId) {
      createDemoParticipantNotification({
        userId: decision.proposed_by,
        actorId: currentUserId,
        type: 'decision_recorded',
        title: 'Decision recorded',
        body: decision.body.length > 100 ? `${decision.body.slice(0, 97)}...` : decision.body,
        projectId: decision.project_id ?? 'unscoped',
        metadata: {
          decision_id: decision.id,
          workspace_id: decision.workspace_id,
          project_id: decision.project_id,
          thread_parent_id: decision.source_thread_id,
          message_id: decision.source_message_id,
          call_session_id: decision.source_call_session_id,
          severity: 'success',
        },
      });
    }
    return { data: updated, error: null };
  }

  // Supabase mode
  try {
    const { error } = await supabase.rpc('decide_decision', {
      p_decision_id: decisionId,
    });

    if (error) {
      return { data: null, error: error.message };
    }

    return getDecisionById(decisionId);
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Supersede Decision
// ─────────────────────────────────────────────────────────────────────────────

export async function supersedeDecision(
  payload: SupersedeDecisionPayload,
  currentUserId: string
): Promise<ServiceResult<{ oldDecision: Decision; newDecision: Decision }>> {
  // Demo mode
  if (isDemoMode) {
    const original = demoDecisions.get(payload.decision_id);
    if (!original) {
      return { data: null, error: 'Decision not found' };
    }
    if (original.status === 'superseded' || original.status === 'invalidated') {
      return { data: null, error: 'Decision already superseded or invalidated' };
    }
    
    const now = new Date().toISOString();
    
    // Create new decision
    const newDecision: Decision = {
      id: generateId(),
      body: payload.new_body,
      title: payload.new_title || original.title,
      project_id: original.project_id,
      workspace_id: original.workspace_id,
      source_thread_id: original.source_thread_id,
      source_message_id: null, // New decision, not linked to message
      source_call_session_id: original.source_call_session_id,
      status: payload.auto_decide ? 'decided' : 'proposed',
      proposed_by: currentUserId,
      proposed_at: now,
      decision_owner_id: original.decision_owner_id,
      decided_by: payload.auto_decide ? currentUserId : null,
      decided_at: payload.auto_decide ? now : null,
      locked_at: payload.auto_decide ? now : null,
      superseded_by_decision_id: null,
      supersede_reason: null,
      invalidated_by: null,
      invalidated_at: null,
      invalidation_reason: null,
      created_at: now,
      updated_at: now,
    };
    
    // Update original
    const updatedOriginal: Decision = {
      ...original,
      status: 'superseded',
      superseded_by_decision_id: newDecision.id,
      supersede_reason: payload.reason,
      updated_at: now,
    };
    
    saveDemoDecision(original.id, updatedOriginal);
    saveDemoDecision(newDecision.id, newDecision);
    
    return { 
      data: { oldDecision: updatedOriginal, newDecision }, 
      error: null 
    };
  }

  // Supabase mode
  try {
    const { data, error } = await supabase.rpc('supersede_decision', {
      p_decision_id: payload.decision_id,
      p_new_body: payload.new_body,
      p_reason: payload.reason,
      p_new_title: payload.new_title || null,
      p_auto_decide: payload.auto_decide || false,
    });

    if (error) {
      return { data: null, error: error.message };
    }

    const newDecisionId = data as string;
    const [oldResult, newResult] = await Promise.all([
      getDecisionById(payload.decision_id),
      getDecisionById(newDecisionId),
    ]);

    if (oldResult.data && newResult.data) {
      return { 
        data: { oldDecision: oldResult.data, newDecision: newResult.data }, 
        error: null 
      };
    }

    return { data: null, error: 'Failed to fetch updated decisions' };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Invalidate Decision
// ─────────────────────────────────────────────────────────────────────────────

export async function invalidateDecision(
  decisionId: string,
  reason: string,
  currentUserId: string
): Promise<ServiceResult<Decision>> {
  if (!reason.trim()) {
    return { data: null, error: 'Invalidation reason is required' };
  }

  // Demo mode
  if (isDemoMode) {
    const decision = demoDecisions.get(decisionId);
    if (!decision) {
      return { data: null, error: 'Decision not found' };
    }
    if (decision.status === 'superseded' || decision.status === 'invalidated') {
      return { data: null, error: 'Decision already superseded or invalidated' };
    }
    
    const now = new Date().toISOString();
    const updated: Decision = {
      ...decision,
      status: 'invalidated',
      invalidated_by: currentUserId,
      invalidated_at: now,
      invalidation_reason: reason,
      updated_at: now,
    };
    
    saveDemoDecision(decisionId, updated);
    return { data: updated, error: null };
  }

  // Supabase mode
  try {
    const { error } = await supabase.rpc('invalidate_decision', {
      p_decision_id: decisionId,
      p_reason: reason,
    });

    if (error) {
      return { data: null, error: error.message };
    }

    return getDecisionById(decisionId);
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Get Decision by ID
// ─────────────────────────────────────────────────────────────────────────────

export async function getDecisionById(
  decisionId: string
): Promise<ServiceResult<Decision>> {
  // Demo mode
  if (isDemoMode) {
    const decision = demoDecisions.get(decisionId);
    if (!decision) {
      return { data: null, error: 'Decision not found' };
    }
    return { data: decision, error: null };
  }

  // Supabase mode
  try {
    const { data, error } = await supabase
      .from('decisions')
      .select('*')
      .eq('id', decisionId)
      .single();

    if (error) {
      return { data: null, error: error.message };
    }

    return { data: mapCanonicalDecision(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Get Decisions for Thread
// ─────────────────────────────────────────────────────────────────────────────

export async function getDecisionsForThread(
  threadId: string,
  includeSuperseded = false
): Promise<ServiceResult<Decision[]>> {
  // Demo mode
  if (isDemoMode) {
    const decisions: Decision[] = [];
    for (const d of demoDecisions.values()) {
      if (d.source_thread_id === threadId) {
        if (includeSuperseded || (d.status !== 'superseded' && d.status !== 'invalidated')) {
          decisions.push(d);
        }
      }
    }
    return { data: decisions.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ), error: null };
  }

  // Supabase mode
  try {
    let query = supabase
      .from('decisions')
      .select('*')
      .eq('source_thread_id', threadId)
      .order('created_at', { ascending: false });

    if (!includeSuperseded) {
      query = query.not('status', 'in', '(superseded,invalidated)');
    }

    const { data, error } = await query;

    if (error) {
      return { data: null, error: error.message };
    }

    return { data: (data || []).map(row => mapCanonicalDecision(row as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Get Decisions for Call Session
// ─────────────────────────────────────────────────────────────────────────────

export async function getDecisionsForCall(
  callSessionId: string,
  includeSuperseded = false
): Promise<ServiceResult<Decision[]>> {
  // Demo mode
  if (isDemoMode) {
    const decisions: Decision[] = [];
    for (const d of demoDecisions.values()) {
      if (d.source_call_session_id === callSessionId) {
        if (includeSuperseded || (d.status !== 'superseded' && d.status !== 'invalidated')) {
          decisions.push(d);
        }
      }
    }
    return { data: decisions.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ), error: null };
  }

  // Supabase mode
  try {
    let query = supabase
      .from('decisions')
      .select('*')
      .eq('source_call_session_id', callSessionId)
      .order('created_at', { ascending: false });

    if (!includeSuperseded) {
      query = query.not('status', 'in', '(superseded,invalidated)');
    }

    const { data, error } = await query;

    if (error) {
      return { data: null, error: error.message };
    }

    return { data: (data || []).map(row => mapCanonicalDecision(row as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Get Decisions for Project
// ─────────────────────────────────────────────────────────────────────────────

export async function getDecisionsForProject(
  projectId: string,
  includeSuperseded = false
): Promise<ServiceResult<Decision[]>> {
  // Demo mode
  if (isDemoMode) {
    const decisions: Decision[] = [];
    for (const d of demoDecisions.values()) {
      // Demo follows the same exact project boundary as connected mode.
      const projectMatches = d.project_id === projectId;
      if (projectMatches) {
        if (includeSuperseded || (d.status !== 'superseded' && d.status !== 'invalidated')) {
          decisions.push(d);
        }
      }
    }
    return { data: decisions.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ), error: null };
  }

  // Supabase mode
  try {
    let query = supabase
      .from('decisions')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (!includeSuperseded) {
      query = query.not('status', 'in', '(superseded,invalidated)');
    }

    const { data, error } = await query;

    if (error) {
      return { data: null, error: error.message };
    }

    return { data: (data || []).map(row => mapCanonicalDecision(row as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

export async function getProjectDecisionIntegrity(
  projectId: string,
): Promise<ServiceResult<DecisionIntegritySummary[]>> {
  if (isDemoMode) {
    const decisionsResult = await getDecisionsForProject(projectId, true);
    if (!decisionsResult.data) return { data: null, error: decisionsResult.error };
    const verifications = await Promise.all(decisionsResult.data.map(buildDemoVerification));
    return {
      data: verifications.map(verification => ({
        decisionId: verification.decisionId,
        valid: verification.valid,
        versionCount: verification.versionCount,
        currentHash: verification.latestHash ?? '',
        lastRecordedAt: verification.records[0]?.recordedAt ?? verification.verifiedAt,
      })),
      error: null,
    };
  }

  try {
    const { data, error } = await supabase.rpc('get_project_decision_integrity', {
      p_project_id: projectId,
    });
    if (error) return { data: null, error: error.message };
    return {
      data: ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
        decisionId: String(row.decision_id),
        valid: Boolean(row.is_valid),
        versionCount: Number(row.version_count ?? 0),
        currentHash: String(row.current_hash ?? ''),
        lastRecordedAt: String(row.last_recorded_at ?? ''),
      })),
      error: null,
    };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

export async function verifyDecisionIntegrity(
  decisionId: string,
): Promise<ServiceResult<DecisionIntegrityVerification>> {
  if (isDemoMode) {
    const decision = demoDecisions.get(decisionId);
    if (!decision) return { data: null, error: 'Decision not found' };
    return { data: await buildDemoVerification(decision), error: null };
  }

  try {
    const { data, error } = await supabase.rpc('verify_decision_integrity', {
      p_decision_id: decisionId,
    });
    if (error) return { data: null, error: error.message };
    const verification = data as unknown as DecisionIntegrityVerification;
    return { data: verification, error: null };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Get Decision by Source Message (for deduplication)
// ─────────────────────────────────────────────────────────────────────────────

export async function getDecisionBySourceMessage(
  messageId: string
): Promise<ServiceResult<Decision | null>> {
  // Demo mode
  if (isDemoMode) {
    for (const d of demoDecisions.values()) {
      if (d.source_message_id === messageId) {
        return { data: d, error: null };
      }
    }
    return { data: null, error: null };
  }

  // Supabase mode
  try {
    const { data, error } = await supabase
      .from('decisions')
      .select('*')
      .eq('source_message_id', messageId)
      .maybeSingle();

    if (error) {
      return { data: null, error: error.message };
    }

    return { data: data ? mapCanonicalDecision(data as Record<string, unknown>) : null, error: null };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Update Decision (for proposed decisions only)
// ─────────────────────────────────────────────────────────────────────────────

export async function updateDecisionBody(
  decisionId: string,
  body: string,
  title?: string
): Promise<ServiceResult<Decision>> {
  // Demo mode
  if (isDemoMode) {
    const decision = demoDecisions.get(decisionId);
    if (!decision) {
      return { data: null, error: 'Decision not found' };
    }
    if (decision.status !== 'proposed' || decision.locked_at) {
      return { data: null, error: 'Cannot edit a decided/locked decision' };
    }
    
    const updated: Decision = {
      ...decision,
      body,
      title: title !== undefined ? title : decision.title,
      updated_at: new Date().toISOString(),
    };
    
    saveDemoDecision(decisionId, updated);
    return { data: updated, error: null };
  }

  // Supabase mode
  try {
    // First check if decision is editable
    const { data: existing, error: fetchError } = await supabase
      .from('decisions')
      .select('status, locked_at')
      .eq('id', decisionId)
      .single();

    if (fetchError) {
      return { data: null, error: fetchError.message };
    }

    if (existing.status !== 'proposed' || existing.locked_at) {
      return { data: null, error: 'Cannot edit a decided/locked decision' };
    }

    const updateData: Record<string, unknown> = { description: body };
    if (title !== undefined) {
      updateData.title = title;
    }

    const { data, error } = await supabase
      .from('decisions')
      .update(updateData)
      .eq('id', decisionId)
      .select()
      .single();

    if (error) {
      return { data: null, error: error.message };
    }

    return { data: mapCanonicalDecision(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Withdraw Decision (record-preserving compatibility boundary)
// ─────────────────────────────────────────────────────────────────────────────

export async function deleteDecision(
  decisionId: string
): Promise<ServiceResult<boolean>> {
  // Demo mode
  if (isDemoMode) {
    const decision = demoDecisions.get(decisionId);
    if (!decision) {
      return { data: null, error: 'Decision not found' };
    }
    if (decision.status !== 'proposed') {
      return { data: null, error: 'Only proposed decisions can be deleted' };
    }
    
    const now = new Date().toISOString();
    saveDemoDecision(decisionId, {
      ...decision,
      status: 'invalidated',
      invalidated_by: decision.proposed_by,
      invalidated_at: now,
      invalidation_reason: 'Withdrawn by proposer',
      updated_at: now,
    });
    return { data: true, error: null };
  }

  // Supabase mode
  try {
    const { error } = await supabase.rpc('withdraw_decision', {
      p_decision_id: decisionId,
    });

    if (error) {
      return { data: null, error: error.message };
    }

    return { data: true, error: null };
  } catch (err) {
    return { data: null, error: (err as Error).message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Seed Demo Data
// ─────────────────────────────────────────────────────────────────────────────

export function seedDemoDecisions(decisions: Decision[]): void {
  for (const d of decisions) {
    saveDemoDecision(d.id, d);
  }
}

export function clearDemoDecisions(): void {
  demoDecisions.clear();
  try { localStorage.removeItem(DEMO_DECISIONS_STORAGE); } catch { /* Test or restricted browser. */ }
}
